import React, { useState } from 'react';
import { startAuthentication } from '@simplewebauthn/browser';
import type { AuthenticationResponseJSON } from '@simplewebauthn/browser';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, LogIn, AlertCircle, Fingerprint } from 'lucide-react';
import { handleWebAuthnError, handleAPIError, showErrorToast, showSuccessToast } from '@/utils/error-handler';
import { toast } from 'sonner';

interface LoginButtonProps {
  onSuccess?: (user: { id: string; username: string; isAdmin?: boolean }, token: string) => void;
  onError?: (error: string) => void;
  className?: string;
}

export function LoginButton({ onSuccess, onError, className }: LoginButtonProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async () => {
    setIsLoading(true);
    setError(null);

    // Show loading toast
    const loadingToast = toast.loading('Starting authentication...');

    try {
      // Step 1: Begin authentication
      const beginResponse = await fetch('/api/auth/login/begin', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!beginResponse.ok) {
        const errorData = await beginResponse.json();
        const apiError = handleAPIError(beginResponse, errorData);
        throw apiError;
      }

      const { options, challengeId } = await beginResponse.json();
      
      // Update loading message
      toast.loading('Please use your authenticator...', { id: loadingToast });

      // Step 2: Start WebAuthn authentication
      let authenticationResponse: AuthenticationResponseJSON;
      try {
        authenticationResponse = await startAuthentication({
          ...options,
          // Ensure proper user verification
          userVerification: 'preferred',
        });
      } catch (webauthnError: any) {
        const error = handleWebAuthnError(webauthnError);
        throw error;
      }

      // Update loading message
      toast.loading('Verifying authentication...', { id: loadingToast });

      // Step 3: Complete authentication
      const completeResponse = await fetch('/api/auth/login/complete', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          credential: authenticationResponse,
          challengeId,
        }),
      });

      if (!completeResponse.ok) {
        const errorData = await completeResponse.json();
        const apiError = handleAPIError(completeResponse, errorData);
        throw apiError;
      }

      const { user, token } = await completeResponse.json();

      // Store token in localStorage for session persistence
      localStorage.setItem('auth-token', token);

      // Dismiss loading toast and show success
      toast.dismiss(loadingToast);
      showSuccessToast('Login successful!', `Welcome back, ${user.username}!`);

      // Call success callback
      onSuccess?.(user, token);

    } catch (err: any) {
      // Dismiss loading toast
      toast.dismiss(loadingToast);
      
      const errorMessage = err.message || 'Login failed. Please try again.';
      setError(errorMessage);
      showErrorToast(err);
      onError?.(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={className}>
      <Button
        onClick={handleLogin}
        disabled={isLoading}
        className="w-full"
        size="lg"
      >
        {isLoading ? (
          <>
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            Authenticating...
          </>
        ) : (
          <>
            <Fingerprint className="mr-2 h-5 w-5" />
            Sign In with Biometrics
          </>
        )}
      </Button>

      {error && (
        <div className="flex items-center gap-2 p-3 mt-3 text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}

export function LoginCard({ onSuccess, onError }: LoginButtonProps) {
  return (
    <Card className="w-full max-w-md mx-auto">
      <CardHeader className="text-center">
        <CardTitle className="flex items-center justify-center gap-2">
          <LogIn className="h-5 w-5" />
          Welcome Back
        </CardTitle>
        <CardDescription>
          Sign in using your device's biometric authentication!
        </CardDescription>
      </CardHeader>
      <CardContent>
        <LoginButton onSuccess={onSuccess} onError={onError} />
        
        <div className="text-xs text-muted-foreground text-center mt-4 space-y-1">
          <p>Use Touch ID, Face ID, or your security key</p>
          <p>No passwords needed!</p>
        </div>
      </CardContent>
    </Card>
  );
}